<template>
  <main class="samsung">
    <Breadcrumb
      :items="breadcrumbItems"
      class="samsung__breadcrumb"
    />

    <SamsungStage
      :data="stage"
      @go-to-section="goToSection"
    />

    <section class="samsung__section samsung__section--smartphones">
      <div
        ref="samsungS25"
        class="samsung__section-anchor"
      />
      <div
        ref="samsungS25Ultra"
        class="samsung__section-anchor"
      />
      <div
        ref="samsungS25Plus"
        class="samsung__section-anchor"
      />

      <VideoHeroContent
        class="samsung__video-section"
        :video="teaserGalaxyFamily.video"
        :title="teaserGalaxyFamily.title"
        :copy="teaserGalaxyFamily.copy"
        :cta="teaserGalaxyFamily.cta"
        :hero-image-align="teaserGalaxyFamily.hero.align"
        :animation-product="teaserGalaxyFamily.hero.animationProduct"
        video-aria-label="Informationsvideo für die Samsung Galaxy Smartphones"
      >
        <CdnImage
          slot="image-fallback"
          class="samsung__bg-image-fallback"
          :src="teaserGalaxyFamily.imageFallback.src"
          :alt="teaserGalaxyFamily.imageFallback.alt"
          lazy
          sizes="sm:100vw lg:80vw"
        />
        <CdnImage
          slot="hero"
          class="samsung__hero-teaser samsung__hero-teaser--galaxy-family"
          :src="teaserGalaxyFamily.hero.src"
          :alt="teaserGalaxyFamily.hero.alt"
          lazy
          sizes="sm:100vw lg:80vw"
        />
      </VideoHeroContent>
    </section>

    <section class="samsung__section">
      <SamsungUspTeaser />
    </section>

    <section class="samsung__section">
      <div
        ref="galaxyWatch7"
        class="samsung__section-anchor"
      />
      <VideoHeroContent
        class="samsung__video-section"
        :row-reverse="watchesTeaser.rowReverse"
        :video="watchesTeaser.video"
        :title="watchesTeaser.title"
        :copy="watchesTeaser.copy"
        :cta="watchesTeaser.cta"
        :hero-image-align="watchesTeaser.hero?.align"
        :animation-product="watchesTeaser.hero?.animationProduct"
        video-aria-label="Informationsvideo für die Galaxy Smartwatches"
      >
        <CdnImage
          v-if="watchesTeaser.hero"
          slot="hero"
          class="samsung__hero-teaser samsung__hero-teaser--watches"
          :src="watchesTeaser.hero.src"
          :alt="watchesTeaser.hero.alt"
          lazy
          sizes="sm:100vw lg:80vw"
        />
      </VideoHeroContent>
    </section>

    <section class="samsung__section">
      <GridContainer>
        <ComparisonTable
          :data="comparisonTableProducts.watches"
          :heading-title="tableWatches.title"
          :sub-title="tableWatches.copy"
          class="samsung__comparison-smartwatches-table"
        >
          <template #default="slotProps">
            <!-- CUSTOM CONTENT SLIDE ITEM & ARRAY DATA STRUCTURE -->
            <ComparisonProduct
              :title="slotProps.data.title"
              :products="slotProps.data.products"
              :products-big="slotProps.data.productsBig"
              :has-same-height="slotProps.sameHeight"
              class="samsung__comparison-smartwatches"
            >
              <template #image="{ itemCurrent: currentImage }">
                <nuxt-link
                  v-if="currentImage.slug"
                  :to="currentImage.slug"
                  :title="currentImage.alt"
                  class="samsung__comparison-smartwatches-link"
                >
                  <CdnImage
                    :alt="currentImage.alt"
                    :src="currentImage.src"
                    lazy
                    render-without-figure
                  />
                </nuxt-link>
                <CdnImage
                  v-else
                  :alt="currentImage.alt"
                  :src="currentImage.src"
                />
              </template>
            </ComparisonProduct>
            <ContentToggle :show-content="slotProps.contentToggle">
              <ComparisonItemValues
                v-for="(val, index) in slotProps.data.values"
                :key="index"
                :title="val.title"
                :content="val.content"
                :list="val.list"
                :additional-info="val.info"
                :is-checked="val.checked"
              />
            </ContentToggle>
          </template>
        </ComparisonTable>
      </GridContainer>

      <SamsungConfigurator />
    </section>

    <section class="samsung__section">
      <div
        ref="galaxyBuds3"
        class="samsung__section-anchor"
      />
      <VideoHeroContent
        class="samsung__video-section"
        :row-reverse="budsTeaser.rowReverse"
        :video="budsTeaser.video"
        :title="budsTeaser.title"
        :copy="budsTeaser.copy"
        :cta="budsTeaser.cta"
        :hero-image-align="budsTeaser.hero.align"
        :animation-product="budsTeaser.hero.animationProduct"
        video-aria-label="Informationsvideo für die Galaxy Buds"
      >
        <CdnImage
          slot="hero"
          class="samsung__hero-teaser samsung__hero-teaser--buds"
          :src="budsTeaser.hero.src"
          :alt="budsTeaser.hero.alt"
          lazy
          sizes="sm:100vw lg:80vw"
        />
      </VideoHeroContent>
    </section>

    <section class="samsung__section">
      <GridContainer>
        <ComparisonTable
          :data="comparisonTableProducts.buds"
          :heading-title="tableBuds.title"
          :sub-title="tableBuds.copy"
        >
          <template #default="slotProps">
            <!-- CUSTOM CONTENT SLIDE ITEM & ARRAY DATA STRUCTURE -->
            <ComparisonProduct
              class="samsung__buds-products"
              :title="slotProps.data.title"
              :products="slotProps.data.products"
              :products-big="slotProps.data.productsBig"
              :has-same-height="slotProps.sameHeight"
            >
              <template #image="{ itemCurrent: currentImage }">
                <nuxt-link
                  v-if="currentImage.slug"
                  :to="currentImage.slug"
                  :title="currentImage.alt"
                  class="samsung__buds-products-link"
                >
                  <CdnImage
                    :alt="currentImage.alt"
                    :src="currentImage.src"
                    lazy
                    render-without-figure
                  />
                </nuxt-link>
                <CdnImage
                  v-else
                  :alt="currentImage.alt"
                  :src="currentImage.src"
                />
              </template>
            </ComparisonProduct>
            <ContentToggle :show-content="slotProps.contentToggle">
              <ComparisonItemValues
                v-for="(val, index) in slotProps.data.values"
                :key="index"
                :title="val.title"
                :content="val.content"
                :list="val.list"
                :additional-info="val.info"
                :is-checked="val.checked"
              />
            </ContentToggle>
          </template>
        </ComparisonTable>
      </GridContainer>
    </section>

    <section class="samsung__section">
      <Heading
        v-if="teaser.offerTilesSmartphones[0]?.mainTitle"
        :text="teaser.offerTilesSmartphones[0]?.mainTitle"
        class="samsung__section-title"
        font-weight="ultra"
        size="lg"
        tag="h2"
        text-uppercase
        align="center"
        spaces-top
        spaces-bottom
      />
      <ProductOfferTileSMContainer
        :items="teaser.offerTilesSmartphones"
        show-available-variant
        tile-type="medium"
        heading-tag="h3"
      />
    </section>

    <section class="samsung__section">
      <Heading
        text="Die Samsung Galaxy Family"
        class="samsung__section-title"
        font-weight="ultra"
        size="lg"
        tag="h2"
        text-uppercase
        align="center"
        spaces-top
      />
      <Heading
        align="center"
        class="samsung__section-copy"
        size="sm"
        tag="h3"
        spaces-top
        spaces-bottom
        font-weight="normal"
      >
        Die perfekte Verbindung für den ganzen Tag
      </Heading>
      <ProductOfferTileSMContainer
        :items="displayedFamilyTiles"
        show-available-variant
        tile-type="small"
        heading-tag="h4"
      />
    </section>

    <section class="samsung__section">
      <SamsungTeasers :teaser-items="teaserBanner" />
    </section>
  </main>
</template>

<script>
import { ref } from 'vue';
import {
  VideoHeroContent,
  Heading,
  GridContainer,
  ComparisonTable,
  ComparisonProduct,
  ComparisonItemValues,
  ContentToggle,
} from '@i22-td-smarthome/component-library';
import { getOffersByGroup, loadOffersForPromotions } from '@/lib/offers-by-group';

import {
  TEASER_BANNER,
  STAGE_DATA,
  SMARTPHONES_GALAXY_FAMILY,
  WATCHES_GALAXY_FAMILY,
  COMPARISON_WATCHES,
  SAMSUNG_GALAXY_BUDS,
  COMPARISON_BUDS,
  PRODUCT_DATA,
} from './product-data';

import Breadcrumb from '@/components/breadcrumb.vue';
import SamsungStage from '@/components/samsung/samsung-stage.vue';
import SamsungUspTeaser from '@/components/samsung/samsung-usp-teaser.vue';
import SamsungTeasers from '@/components/samsung/samsung-teasers.vue';
import CdnImage from '@/components/cdn-image.vue';
import ProductOfferTileSMContainer from '@/components/product-offer-tile-s-m-container.vue';
import { Track } from '~/helpers/page-tracking';
import { getHeader } from '~/helpers/page-header-compositon';
import SamsungConfigurator from '~/components/smartwatch-config/samsung-configurator.vue';

export default {
  name: 'SamsungManufacturer',
  components: {
    SamsungConfigurator,
    Breadcrumb,
    SamsungStage,
    VideoHeroContent,
    CdnImage,
    SamsungUspTeaser,
    GridContainer,
    ComparisonTable,
    ComparisonProduct,
    ComparisonItemValues,
    ContentToggle,
    ProductOfferTileSMContainer,
    Heading,
    SamsungTeasers,
  },
  setup() {
    Track({ page_type: 'theme.landing' });

    getHeader({
      title: ref('Samsung Galaxy Family entdecken'),
      description: ref(
        'Die Geräte der Samsung Galaxy Family arbeiten perfekt zusammen. Entdecke jetzt die Vorteile und finde mit dem Galaxy Watch Konfigurator Dein Wunschmodell!'
      ),
    });
  },
  async asyncData() {
    const offers = await loadOffersForPromotions(PRODUCT_DATA);

    return {
      offers,
      teaserBanner: TEASER_BANNER,
      stage: STAGE_DATA,
      teaserGalaxyFamily: SMARTPHONES_GALAXY_FAMILY,
      watchesTeaser: WATCHES_GALAXY_FAMILY,
      tableWatches: COMPARISON_WATCHES,
      tableBuds: COMPARISON_BUDS,
      budsTeaser: SAMSUNG_GALAXY_BUDS,
      productData: PRODUCT_DATA,
      breadcrumbItems: [
        {
          label: 'Samsung',
          to: 'samsung',
        },
      ],
    };
  },
  computed: {
    teaser() {
      return {
        offerTilesSmartphones: this.getOffersByGroup(this.offers, 'smartphones'),
      };
    },
    displayedFamilyTiles() {
      return ['family_first', 'family_second', 'family_third']
        .map((group) => getOffersByGroup(this.offers, group, 1))
        .flat();
    },
    comparisonTableProducts() {
      const watchOffers = this.getOffersByGroup(this.offers, 'comparison_watches');
      const budsOffers = this.getOffersByGroup(this.offers, 'comparison_buds');

      return {
        watches: this.tableWatches?.items?.map((item) => ({
          ...item,
          products: item.products?.map((product) => {
            const matchingOfferSlug = watchOffers.find(
              (offer) => offer.articleNumber === product.articleNumber
            )?.offer?.product?.slug;
            return {
              ...product,
              slug: matchingOfferSlug
                ? `/geraete/${matchingOfferSlug}`
                : '/wearables/smartwatches/_samsung',
            };
          }),
          productsBig: item.productsBig?.map((product) => {
            const matchingOfferSlug = watchOffers.find(
              (offer) => offer.articleNumber === product.articleNumber
            )?.offer?.product?.slug;
            return {
              ...product,
              slug: matchingOfferSlug
                ? `/geraete/${matchingOfferSlug}`
                : '/wearables/smartwatches/_samsung',
            };
          }),
        })),
        buds: this.tableBuds?.items?.map((item) => ({
          ...item,
          products: item.products?.map((product) => {
            const matchingOfferSlug = budsOffers.find(
              (offer) => offer.articleNumber === product.articleNumber
            )?.offer?.product?.slug;
            return {
              ...product,
              slug: matchingOfferSlug
                ? `/geraete/${matchingOfferSlug}`
                : '/zubehoer/kopfhoerer/_samsung',
            };
          }),
        })),
      };
    },
  },
  methods: {
    getOffersByGroup,
    goToSection(refString) {
      this.$nextTick(() => {
        if (!this.$refs[refString]) return;
        this.$refs[refString].scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.samsung {
  :deep(.video-hero-content__hero-image) {
    width: 90%;
    @include mq($mq-sm) {
      width: 70%;
    }
  }

  :deep(.video-hero-content__content .link-button--primary) {
    @include mq($mq-md) {
      padding: 0.5 * $grid-base 2 * $grid-base;
    }
  }

  &__section {
    position: relative;
    padding: $grid-base * 2 0;
    @include mq($mq-md) {
      padding: $grid-base * 4 0;
    }

    &--smartphones {
      :deep(.video-hero-content__hero-image) {
        width: 90%;
      }
    }

    &-copy {
      font-weight: normal;
    }

    &-anchor {
      position: absolute;
      left: 0;
      top: -70px;
    }
  }

  &__buds-products {
    :deep(.comparison-product__list, .comparison-product__bottom) {
      padding-top: 0;
    }

    &-link {
      display: block;
      width: 100%;
      img {
        display: block;
        width: 100%;
        aspect-ratio: 5 / 4;
      }
    }
  }
  &__video-section {
    margin-bottom: $grid-base * 4;
    @include mq($mq-md) {
      margin-bottom: $grid-base * 6;
    }

    :deep(.video-player) {
      --play-button-position-left: calc(100% - 50px);
    }
  }

  &__comparison-smartwatches {
    &-table {
      @include mq($mq-lg) {
        --data-cols: 4;
      }
    }
    &-link {
      display: block;
    }
    // comparison table has no image dimensions set:
    :deep(.comparison-product__image) img {
      width: 172px;
      height: 244px;
      display: block;
    }
  }

  &__hero-teaser {
    &--galaxy-family {
      @include mq($mq-lg) {
        text-align: right;
      }
      :deep(img) {
        aspect-ratio: 1171 / 633;
        width: 80%;
      }
    }

    &--buds {
      :deep(img) {
        aspect-ratio: 1024 / 327;
        width: 100%;
      }
    }
  }

  &__bg-image-fallback {
    :deep(img) {
      width: 100%;
      aspect-ratio: 320 / 139;
    }
  }
}
</style>
